import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { LayoutService } from "../../services/layout.service";
import { SnackBarService } from '../../services/snack-bar.service';
import { from, forkJoin, of, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Utils } from '../../services/utils.service';

interface Photo {
    id?: number,
    name?: string,
    stand: number,
    planogram: number,
    file: string,
	clientSignature?: string
}

interface PhotoLoader {
    file: File,
    status: 'loading' | 'failed',
    retrying?: boolean,
    cancelled?: boolean,
	signature: string
}

interface InputData {
    planogram: number;
    trackerWarning?: boolean;
}

@Component({
    selector: 'app-photo-report-modal',
    templateUrl: './photo-report-modal.component.html',
    styleUrls: ['./photo-report-modal.component.scss'],
    standalone: false
})
export class PhotoReportModalComponent implements OnInit {
    photos: Array<Photo> = [];
    photosLoader: Array<PhotoLoader> = [];
    comment: string = "";
    timeTrackingWarning: boolean = false;
	hasFailedUploads: boolean = false;

    constructor(
        private dialogRef: MatDialogRef<PhotoReportModalComponent>,
        private Layout: LayoutService,
		private snackBarService: SnackBarService,
        @Inject(MAT_DIALOG_DATA) public data: InputData,
    ) {}

	private updateFailedUploadsFlag(): void {
		this.hasFailedUploads = this.photosLoader.some((p: PhotoLoader): boolean => p.status === 'failed');
	}

	private buildFileSignature(file: File): string {
		return `${file.name}:${file.size}:${file.lastModified}`;
	}

	private isDuplicateFile(file: File): boolean {
		const signature: string = this.buildFileSignature(file);

		return this.photosLoader.some((loader: PhotoLoader): boolean => loader.signature === signature)
			|| this.photos.some((photo: Photo): boolean => photo.clientSignature === signature);
	}

    ngOnInit(): void {
        this.timeTrackingWarning = !!this.data.trackerWarning;
    }

    onNoClick(): void {
        this.dialogRef.close(false);
    }

    removePhoto(photo: Photo): void {
        this.photos = this.photos.filter((i: Photo): boolean => i !== photo);
    }

    removePhotoLoader(photoLoader: PhotoLoader): void {
        photoLoader.cancelled = true;
        this.photosLoader = this.photosLoader.filter((i: PhotoLoader): boolean => i != photoLoader);
		this.updateFailedUploadsFlag();
    }

	private handleSuccessfulUpload(photo: Photo, loader: PhotoLoader): void {
		if (!loader.cancelled) {
			photo.name = decodeURIComponent(photo.file.split('/').pop()?.split('.').slice(0, -1).join('.') || '');
			photo.clientSignature = loader.signature;
			this.photos.push(photo);
			this.photosLoader = this.photosLoader.filter((l: PhotoLoader): boolean => l !== loader);
		}
	}

    async uploadFile(event: Event): Promise<void> {
		const input: HTMLInputElement = event.target as HTMLInputElement;
        const files: FileList = input.files;
        if (!files) return;

		const newFiles: Array<File> = Array.from(files).filter((file: File): boolean => !this.isDuplicateFile(file));
		input.value = '';

		if (!newFiles.length) return;

		const loaders: Array<PhotoLoader> = [];
		for (const file of newFiles) {
			const signature: string = this.buildFileSignature(file);
			let processedFile: File = file;
			const extension = file.name.split('.').pop()?.toLowerCase();

			if (['heic', 'heif'].includes(extension)) {
				processedFile = await Utils.heicToJpeg(file);
			}

			processedFile = await Utils.resizeImage(processedFile);

			const loader: PhotoLoader = { file: processedFile, status: 'loading', signature };
			this.photosLoader.push(loader);
			loaders.push(loader);
		}

		const uploadObservables: Array<Observable<Photo>> = loaders.map((loader: PhotoLoader): Observable<Photo> => {
			const formData = new FormData();
			formData.append('file', loader.file, loader.file.name);

			return from(this.Layout.uploadFile(formData)).pipe(
				tap((photo: Photo): void => {
					this.handleSuccessfulUpload(photo, loader);
					this.updateFailedUploadsFlag();
				}),
				catchError((): Observable<null> => {
					loader.status = 'failed';
					this.snackBarService.setSnackBarNotification({ messageText: "UPLOAD_FILE_FAILED" });
					this.updateFailedUploadsFlag();
					return of(null);
				})
			);
		});

		forkJoin(uploadObservables).subscribe();
	}

	retryUpload(loader: PhotoLoader): void {
		loader.status = 'loading';
		loader.retrying = true;

		const formData = new FormData();
		formData.append('file', loader.file, loader.file.name);

		from(this.Layout.uploadFile(formData)).pipe(
			tap((photo: Photo): void => {
				this.handleSuccessfulUpload(photo, loader);
				this.updateFailedUploadsFlag();
			}),
			catchError((): Observable<null> => {
				loader.status = 'failed';
				loader.retrying = false;
				this.snackBarService.setSnackBarNotification({ messageText: "UPLOAD_FILE_FAILED" });
				this.updateFailedUploadsFlag();
				return of(null);
			}),
		).subscribe();
	}

	apply(): void {
		if (!this.photos.length) return;

		forkJoin(
			this.photos.map((p: Photo): Observable<void> => from(this.Layout.updateFileName(p.id, p.name)))
		).pipe(
			finalize((): void => {
				this.dialogRef.close({
					planogram: this.data.planogram,
					photos: this.photos.map((i: Photo): number => i.id),
					comment: this.comment
				});
			})
		).subscribe();
	}
}
